import { ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import cc from "classcat";
import { Button, LoadingSkeleton } from "@narmi/design_system";
import styles from "./SectionCard.module.scss";

interface TitleProps {
  text: string;
  button?: ReactNode;
  className?: string;
  isExpanded?: boolean;
  setIsExpanded?: (newState: (arg: boolean) => boolean) => void;
  testId?: string;
  titleIcon?: ReactNode;
}

const Title = ({
  text,
  button,
  className,
  isExpanded,
  setIsExpanded,
  testId,
  titleIcon
}: TitleProps) => {
  const { l10n } = useLocalization();
  return (
    <div className={className}>
      <div
        className="alignChild--left--center"
        style={{ flexWrap: "wrap", gap: "var(--space-xxs)" }}
      >
        <h4
          className="nds-sans"
          style={{ marginRight: "auto" }}
          data-testid={testId}
        >
          {text}
          {titleIcon && isExpanded && (
            <span className="margin--left--xxs">{titleIcon}</span>
          )}
        </h4>
        {button}
        {setIsExpanded && (
          <Button
            kind="plain"
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            label={
              isExpanded
                ? l10n.getString("button-hide", null, "Hide")
                : l10n.getString("button-view", null, "View")
            }
          />
        )}
      </div>
    </div>
  );
};

type LoadingContent = "paragraph" | "headerText";
type PaddingSize = "xs" | "s" | "m" | "l" | "xl" | null;
type LoadingSize = "small" | "medium" | "large";
type Kind = "transactions";

interface SectionCardProps {
  children?: ReactNode;
  isLoading?: boolean;
  title?: string;
  isExpanded?: boolean;
  setIsExpanded?: (newState: (arg: boolean) => boolean) => void;
  loadingContent?: LoadingContent;
  showLoadingTitle?: boolean;
  loadingLines?: number;
  loadingSize?: LoadingSize;
  paddingSize?: PaddingSize;
  classNames?: string;
  kind?: Kind;
  testId?: string;
  titleIcon?: ReactNode;
}

const SectionCard = ({
  children,
  isLoading = false,
  title,
  isExpanded = true,
  setIsExpanded,
  loadingContent = "paragraph",
  showLoadingTitle = false,
  loadingLines = 3,
  loadingSize = "medium",
  paddingSize = "l",
  classNames = "",
  kind,
  testId = "",
  titleIcon
}: SectionCardProps) => {
  /* if we are done loading there is no content, then return null */
  if (!isLoading && !children) {
    return null;
  }

  return (
    <div
      data-testid={testId}
      className={cc([
        { [`padding--all--${paddingSize}`]: paddingSize },
        `${styles.sectionCard} section-card ${classNames}`,
        { [styles.transactionsCard]: kind === "transactions" }
      ])}
    >
      <LoadingSkeleton
        isLoading={isLoading}
        content={loadingContent}
        showTitle={showLoadingTitle}
        lines={loadingLines}
        size={loadingSize}
      >
        {title && (
          <Title
            text={title}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            titleIcon={titleIcon}
          />
        )}
        {isExpanded && children}
      </LoadingSkeleton>
    </div>
  );
};
SectionCard.Title = Title;

export default SectionCard;
